import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
const specialistsServices = [{
  id: '01',
  title: 'Profile Submission & Review',
  titleUa: 'Подання та перегляд профілю',
  description: 'We review professional profiles and documentation to match specialists with relevant tasks and projects',
  descriptionUa: 'Ми переглядаємо професійні профілі та документацію, щоб підібрати спеціалістів до відповідних завдань та проектів'
}, {
  id: '02',
  title: 'Documentation Guidance',
  titleUa: 'Консультації з документації',
  description: 'Support with understanding requirements, documentation structure, and compliance expectations',
  descriptionUa: 'Підтримка у розумінні вимог, структури документації та очікувань щодо відповідності'
}, {
  id: '03',
  title: 'Project Coordination',
  titleUa: 'Координація проектів',
  description: 'Clear communication throughout the process of task or project involvement',
  descriptionUa: 'Чітка комунікація протягом процесу залучення до завдань або проектів'
}, {
  id: '04',
  title: 'Professional Support',
  titleUa: 'Професійна підтримка',
  description: 'Guidance and assistance during cooperation with companies and project teams',
  descriptionUa: 'Керівництво та допомога під час співпраці з компаніями та проектними командами'
}, {
  id: '05',
  title: 'Long-Term Opportunities',
  titleUa: 'Довгострокові можливості',
  description: 'Building structured long-term professional cooperation based on experience and reliability',
  descriptionUa: 'Побудова структурованої довгострокової професійної співпраці на основі досвіду та надійності'
}];
const companiesServices = [{
  id: '01',
  title: 'Project & Operational Support',
  titleUa: 'Проектна та операційна підтримка',
  description: 'We support ongoing operations and projects by taking responsibility for specific tasks, coordination, and execution',
  descriptionUa: 'Ми підтримуємо поточні операції та проекти, беручи на себе відповідальність за конкретні завдання, координацію та виконання'
}, {
  id: '02',
  title: 'Documentation & Compliance',
  titleUa: 'Документація та відповідність',
  description: 'Structured handling of documentation, requirements, and approvals according to project and regulatory needs',
  descriptionUa: 'Структурована обробка документації, вимог та затверджень відповідно до проектних та регуляторних потреб'
}, {
  id: '03',
  title: 'Coordination & Process Management',
  titleUa: 'Координація та управління процесами',
  description: 'Clear communication and control between departments, contractors, and involved parties',
  descriptionUa: 'Чітка комунікація та контроль між відділами, підрядниками та залученими сторонами'
}, {
  id: '04',
  title: 'Technical & Analytical Support',
  titleUa: 'Технічна та аналітична підтримка',
  description: 'Analysis, reporting, and structured information to support operational and management decisions',
  descriptionUa: 'Аналіз, звітність та структурована інформація для підтримки операційних та управлінських рішень'
}, {
  id: '05',
  title: 'Non-Standard & Urgent Tasks',
  titleUa: 'Нестандартні та термінові завдання',
  description: 'Fast response to complex or time-sensitive requests that require experience and attention',
  descriptionUa: 'Швидка реакція на складні або термінові запити, які потребують досвіду та уваги'
}];
export function ClientsSection() {
  const [activeTab, setActiveTab] = useState<'specialists' | 'companies'>('specialists');
  const [language, setLanguage] = useState<'en' | 'ua'>('en');
  const currentServices = activeTab === 'specialists' ? specialistsServices : companiesServices;
  return <section id="services" className="w-full py-16 px-6 md:px-12 bg-[var(--color-dark)]" data-id="element-3">
      <div className="max-w-7xl mx-auto" data-id="element-4">
        {/* Services pill */}
        <div className="inline-block px-4 py-1.5 border border-gray-500 rounded-full mb-8" data-id="element-5">
          <span className="text-sm font-medium text-white" data-id="element-6">Services</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-12" data-id="element-7">
          {/* Left side */}
          <div className="lg:w-2/5 flex flex-col" data-id="element-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-9">
              How We Support
              <br data-id="element-10" />
              Our Clients
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-4" data-id="element-11">
              We handle operational, technical, and organizational tasks for
              marine and industrial projects.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mb-8" data-id="element-12">
              From documentation and coordination to complex, non-standard
              requests — we focus on solutions, not labels.
            </p>

            {/* World Map Image - Balanced position */}
            <div className="relative w-full mt-8" data-id="element-13">
              <img src="https://cdn.magicpatterns.com/uploads/xogzbbaVVmvGP66T2eJr5W/Group_563.png" alt="World map with connections" className="w-full h-auto opacity-40" data-id="element-14" />
            </div>
          </div>

          {/* Right side - White card with tabs */}
          <div className="lg:w-3/5" data-id="element-15">
            {/* Centered Tab Switcher */}
            <div className="flex justify-center mb-6" data-id="element-16">
              <div className="bg-gray-700/50 p-1.5 rounded-xl inline-flex gap-1" data-id="element-17">
                <button onClick={() => setActiveTab('specialists')} className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'specialists' ? 'bg-[var(--color-tan)] text-white shadow-lg' : 'text-gray-300 hover:text-white'}`} data-id="element-18">
                  For Specialists
                </button>
                <button onClick={() => setActiveTab('companies')} className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'companies' ? 'bg-[var(--color-tan)] text-white shadow-lg' : 'text-gray-300 hover:text-white'}`} data-id="element-19">
                  For Companies
                </button>
              </div>
            </div>

            {/* Services list card */}
            <div className="bg-white rounded-2xl p-6 md:p-8" data-id="element-20">
              <div className="space-y-6" data-id="element-21">
                {currentServices.map(service => <div key={service.id} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0" data-id="element-22">
                    <div className="flex gap-4" data-id="element-23">
                      <span className="text-[var(--color-tan)] font-medium text-sm flex-shrink-0" data-id="element-24">
                        {service.id}
                      </span>
                      <div className="flex-1" data-id="element-25">
                        <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-26">
                          {language === 'en' ? service.title : service.titleUa}
                        </h3>
                        <p className="text-sm text-[var(--color-text-light)]" data-id="element-27">
                          {language === 'en' ? service.description : service.descriptionUa}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>

              {/* Learn More Button - Centered */}
              <div className="mt-8 pt-6 border-t border-gray-100 flex justify-center" data-id="element-28">
                <a href={activeTab === 'specialists' ? '/specialists' : '/companies'} className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-29">
                  {activeTab === 'specialists' ? 'Learn More About Specialists' : 'Learn More About Companies'}
                  <ArrowRight className="w-4 h-4" data-id="element-30" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}